import { Link } from "@inertiajs/react"
import { Lock01, Share03 } from "@untitledui/icons"

import { InlinePreviewShell } from "@/components/player/shells/inline-preview-shell"
import { Avatar } from "@/components/ui/avatar"
import { Badge, BadgeWithIcon } from "@/components/ui/badge"
import { formatRelativeTime } from "@/lib/utils/"
import { profilePath } from "@/routes"

import { suitFor } from "./conversation-suits"
import type { FeedEntry } from "./feed-model"

interface SuggestionFeedItemProps {
  entry: FeedEntry
  /**
   * Present when the entry's prompt is still suggestable: the prompt chip
   * becomes the feed→composer bridge (select the card, scroll to the
   * composer). Absent → the chip renders as a plain label.
   */
  onPromptSelect?: (promptId: number) => void
}

export function SuggestionFeedItem({
  entry,
  onPromptSelect,
}: SuggestionFeedItemProps) {
  const metadata = (
    <FeedItemMeta entry={entry} onPromptSelect={onPromptSelect} />
  )

  return (
    <article className="bg-surface-elevated rounded-xl p-4 shadow-xs ring-1 ring-black/5 sm:p-5">
      {entry.playable ? (
        <InlinePreviewShell item={entry.playable} metadata={metadata} />
      ) : (
        <div className="flex min-w-0 flex-col gap-1">
          {entry.url && (
            <a
              href={entry.url}
              rel="noreferrer"
              target="_blank"
              className="text-primary w-fit text-sm font-medium underline-offset-2 hover:underline"
            >
              {entry.url}
            </a>
          )}
          {metadata}
        </div>
      )}
    </article>
  )
}

function FeedItemMeta({ entry, onPromptSelect }: SuggestionFeedItemProps) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Avatar
          name={entry.contributorName}
          avatarUrl={entry.contributorAvatarUrl}
          size="xs"
        />
        {entry.contributorSlug ? (
          <Link
            href={profilePath(entry.contributorSlug)}
            className="text-primary inline-flex items-center gap-1 text-sm font-medium"
          >
            <span>{entry.contributorName}</span>
            <Share03 aria-hidden="true" className="size-3 shrink-0" />
          </Link>
        ) : (
          <span className="text-primary text-sm font-medium">
            {entry.contributorName}
          </span>
        )}
        {entry.createdAt && (
          <span className="text-quaternary text-xs">
            {formatRelativeTime(entry.createdAt)}
          </span>
        )}
        <ToneBadge tone={entry.tone} />
      </div>

      {entry.notes && (
        <p className="border-batyr-red-pencil/50 text-secondary border-l-2 pl-3 text-sm">
          {entry.notes}
        </p>
      )}

      <PromptChip entry={entry} onPromptSelect={onPromptSelect} />
    </div>
  )
}

function ToneBadge({ tone }: { tone: FeedEntry["tone"] }) {
  switch (tone) {
    case "accepted":
      return (
        <Badge size="sm" color="success" type="pill-color">
          On the playlist
        </Badge>
      )
    case "curator":
      return (
        <BadgeWithIcon
          size="sm"
          color="gray"
          type="pill-color"
          iconLeading={Lock01}
        >
          Curator pick
        </BadgeWithIcon>
      )
    case "pending":
      // Pending is the default state of a conversation — no badge; the
      // whisper in the meta row is all the status this needs.
      return <span className="text-quaternary text-xs">awaiting review</span>
  }
}

function PromptChip({ entry, onPromptSelect }: SuggestionFeedItemProps) {
  const { prompt } = entry
  const suit = suitFor(prompt.content_type)
  const face = (
    <>
      <suit.icon aria-hidden className="size-3" />
      <span className="max-w-56 truncate">{prompt.title}</span>
    </>
  )
  const chipClasses =
    "text-tertiary flex w-fit items-center gap-1.5 rounded-full bg-black/[0.04] px-2.5 py-1 text-xs font-medium"

  if (onPromptSelect && prompt.status.can_suggest) {
    return (
      <button
        type="button"
        onClick={() => onPromptSelect(prompt.id)}
        className={`${chipClasses} outline-focus-ring hover:text-secondary cursor-pointer hover:bg-black/[0.07] focus-visible:outline-2 focus-visible:outline-offset-2`}
        aria-label={`Answer this card: ${prompt.title}`}
      >
        {face}
      </button>
    )
  }

  return <span className={chipClasses}>{face}</span>
}
